import type React from 'react';
import { type ReactNode, useState } from 'react';
import { Button, cn, Modal, Spinner } from '@skupreme/shared/ui';
import { DangerBorderButton, DangerButton } from '@elements/Button';
import useUrlPermission from '@hooks/url-permission';
import useBodyModalOpen from '@hooks/useBodyModalOpen';

//#region --- Interfaces ---

interface IDeleteConfirmWithButtonProps {
  buttonClass?: string;
  buttonText?: string | React.ReactNode;
  content?: string;
  deleteButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement> & {
    disabled?: boolean;
  };
  onConfirm: () => void;
  size?: 'mini' | 'tiny' | 'small' | 'large';
  title?: string;
  confirmText?: string;
  cancelText?: string;
  filledButton?: boolean;
  /** Disables the delete button, e.g. on a read-only row. */
  disabled?: boolean;
}

interface IDeleteConfirmCenterPopupProps {
  size?: 'mini' | 'tiny' | 'small' | 'large';
  onConfirm: () => void;
  content?: string;
  title?: string;
  open?: boolean;
  onClose: (confirmed: boolean) => void;
  noButtonTitle?: string;
  confirmLabel?: string;
}

interface IDeleteConfirmPopupProps {
  size?: 'mini' | 'tiny' | 'small' | 'large';
  onConfirm: () => void;
  content?: string | React.ReactNode;
  title?: string;
  open?: boolean;
  onClose: (confirmed: boolean) => void;
  noButtonTitle?: string;
  confirmLabel?: string;
  loading?: boolean;
}

interface IInfoPopupProps {
  size?: 'mini' | 'tiny' | 'small' | 'large';
  onConfirm: () => void;
  content?: string;
  title?: string;
  buttonText?: string;
  open?: boolean;
  onClose?: () => void;
}

interface IDeleteModalProps {
  onConfirm?: () => void;
  confirmButtonTitle?: string;
  cancelButtonTitle?: string;
  content?: string | React.ReactNode;
  open: boolean;
  close: (value: boolean) => void;
}

interface IDeleteConfirmWithoutHeaderProps {
  btnClassName?: string;
  buttonText?: string;
  cancelButtonTitle?: string;
  confirmButtonTitle?: string;
  content?: string;
  disabledButton?: boolean;
  onConfirm: () => void;
}

//#endregion

//#region --- Components ---

const SIZES = { mini: 'sm', tiny: 'sm', small: 'md', large: 'lg' } as const;
type LegacySize = keyof typeof SIZES;

type ConfirmDialogProps = {
  open: boolean;
  size?: LegacySize;
  title?: ReactNode;
  content?: ReactNode;
  /** No/Cancel, Esc and the backdrop all land here. */
  onCancel?: () => void;
  onConfirm: () => void;
  cancelLabel?: ReactNode;
  confirmLabel: ReactNode;
  loading?: boolean;
  centered?: boolean;
  confirmVariant?: 'primary' | 'danger';
};

/** The one dialog behind every variant below, on the kit Modal. */
function ConfirmDialog({
  open,
  size = 'mini',
  title,
  content,
  onCancel,
  onConfirm,
  cancelLabel,
  confirmLabel,
  loading = false,
  centered = false,
  confirmVariant = 'primary',
}: ConfirmDialogProps) {
  return (
    <Modal
      isOpen={open}
      onOpenChange={isOpen => !isOpen && onCancel?.()}
      isDismissable={!loading && !!onCancel}
      size={SIZES[size]}
      title={title || undefined}
      aria-label={title ? undefined : 'Confirm'}
      bodyClassName={cn(centered && 'sk:text-center')}
      footer={
        <div
          className={cn(
            'sk:flex sk:w-full sk:gap-2',
            centered ? 'sk:justify-center' : 'sk:justify-end',
          )}
        >
          {cancelLabel !== undefined && onCancel && (
            <Button isDisabled={loading} onPress={onCancel}>
              {cancelLabel}
            </Button>
          )}
          <Button
            variant={confirmVariant}
            isPending={loading}
            onPress={onConfirm}
          >
            {loading && <Spinner size="sm" />}
            {confirmLabel}
          </Button>
        </div>
      }
    >
      {content}
    </Modal>
  );
}

export function DeleteConfirmWithButton({
  buttonClass = '',
  buttonText = 'Delete',
  content = 'Are you sure you want to delete?',
  deleteButtonProps = {},
  onConfirm,
  size = 'mini',
  title = 'Delete',
  confirmText = 'Yes',
  cancelText = 'No',
  filledButton = false,
  disabled: disabledProp = false,
}: IDeleteConfirmWithButtonProps) {
  const { disabled: deleteBtn } = deleteButtonProps ?? {};
  const { disabled } = useUrlPermission();
  const [open, setOpen] = useState(false);

  useBodyModalOpen(open);

  const triggerProps = {
    type: 'button' as const,
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setOpen(true);
    },
    className: `btn-delete ${buttonClass}`,
    ...deleteButtonProps,
    disabled: deleteBtn || disabled || disabledProp,
  };

  return (
    <>
      {filledButton ? (
        <DangerButton {...triggerProps}>{buttonText}</DangerButton>
      ) : (
        <DangerBorderButton {...triggerProps}>{buttonText}</DangerBorderButton>
      )}
      <ConfirmDialog
        open={open}
        size={size}
        title={title}
        content={content}
        cancelLabel={cancelText}
        confirmLabel={confirmText}
        onCancel={() => setOpen(false)}
        onConfirm={() => {
          setOpen(false);
          onConfirm();
        }}
      />
    </>
  );
}

export function DeleteConfirmCenterPopup({
  size = 'mini',
  onConfirm,
  content = '',
  title = 'Are you sure you’d like to delete?',
  open = false,
  onClose,
  noButtonTitle = 'No',
  confirmLabel = 'Yes',
}: IDeleteConfirmCenterPopupProps) {
  return (
    <ConfirmDialog
      open={open}
      size={size}
      title={title}
      content={content}
      centered
      cancelLabel={noButtonTitle}
      confirmLabel={confirmLabel}
      onCancel={() => onClose(false)}
      onConfirm={() => {
        onConfirm();
        onClose(true);
      }}
    />
  );
}

export function DeleteConfirmPopup({
  size = 'mini',
  onConfirm,
  content = 'Are you sure to delete?',
  title = 'Delete',
  open = false,
  onClose,
  noButtonTitle = 'No',
  confirmLabel = 'Yes',
  loading = false,
}: IDeleteConfirmPopupProps) {
  return (
    <ConfirmDialog
      open={open}
      size={size}
      title={title}
      content={content}
      loading={loading}
      cancelLabel={noButtonTitle}
      confirmLabel={confirmLabel}
      onCancel={() => onClose(false)}
      onConfirm={() => {
        onConfirm();
        onClose(true);
      }}
    />
  );
}

/** A message with one button; it closes only through that button, as before. */
export function InfoPopup({
  size = 'mini',
  onConfirm,
  content = 'There are new messages',
  title = 'Message',
  buttonText = 'Okay',
  open = false,
}: IInfoPopupProps) {
  return (
    <ConfirmDialog
      open={open}
      size={size}
      title={title}
      content={content}
      confirmLabel={buttonText}
      onConfirm={onConfirm}
    />
  );
}

export function DeleteModal({
  onConfirm,
  confirmButtonTitle = 'Yes, Delete',
  cancelButtonTitle = 'Cancel',
  content = 'Are you sure to delete?',
  open,
  close,
}: IDeleteModalProps) {
  return (
    <ConfirmDialog
      open={open}
      size="tiny"
      title={content}
      centered
      cancelLabel={cancelButtonTitle}
      confirmLabel={confirmButtonTitle}
      onCancel={() => close(false)}
      onConfirm={() => {
        close(false);
        onConfirm?.();
      }}
    />
  );
}

export function DeleteConfirmWithoutHeader({
  btnClassName = '',
  buttonText = 'Delete',
  cancelButtonTitle = 'Cancel',
  confirmButtonTitle = 'Yes, Delete',
  content = 'Are you sure to delete?',
  disabledButton,
  onConfirm,
}: IDeleteConfirmWithoutHeaderProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <DangerButton
        type="button"
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        className={`btn-delete ${btnClassName}`}
        disabled={disabledButton}
      >
        {buttonText}
      </DangerButton>
      <DeleteModal
        close={() => setOpen(false)}
        open={open}
        onConfirm={onConfirm}
        cancelButtonTitle={cancelButtonTitle}
        confirmButtonTitle={confirmButtonTitle}
        content={content}
      />
    </>
  );
}

//#endregion
